import React, { useMemo } from 'react';
import { Text, useTheme } from '@chakra-ui/react';
import { useIntl } from 'react-intl';

import { Headline } from '@ldfeplatform/componentlibrary.ui.molecules.headline';
import { Link } from '@ldfeplatform/componentlibrary.ui.atoms.link';

import styles from './pharmacist-information.module.scss';
import CardInformation from './card-information';
import { ACCREDITED_BY_LOGO_ON, COLLEGE_LINKS, PHARMACY_LICENSE, PROFILE_PICTURE } from './constants';
import { PharmacistInformationProps, Province } from './pharmacist-information.interface';
import { STORE_TYPES } from '../store-details/constants';

const PharmacistInformation = ({
  storeId,
  pharmacistInformation,
  province = 'ON' as Province,
  storeType,
}: PharmacistInformationProps) => {
  const { common } = useTheme();
  const { formatMessage } = useIntl();

  const pharmacistOwner = {
    image: `${PROFILE_PICTURE}${storeId}.jpg`,
    imageAlt: formatMessage({ id: 'pharmacistOwner' }),
    title: formatMessage({ id: 'pharmacistOwner' }),
    subtitle: pharmacistInformation?.data?.name,
  };

  const pharmacistLicense = {
    image: null,
    imageAlt: formatMessage({ id: 'pharmacistLicense' }),
    title: formatMessage({ id: 'pharmacistLicense' }),
    subtitle: (
      <Link data-testid="pharmacy-license-number" href={`${PHARMACY_LICENSE}${storeId}.pdf`}>
        {pharmacistInformation?.data?.pharmacistLicenseNumber}
      </Link>
    ),
  };

  const pharmacyLicensee = {
    image: null,
    imageAlt: formatMessage({ id: 'pharmacyLicensee' }),
    title: formatMessage({ id: 'pharmacyLicensee' }),
    subtitle: pharmacistInformation?.data?.licenseeName,
  };

  // Card configurations for each province
  const cardConfigurations: Record<string, any[]> = {
    AB: [pharmacistOwner, pharmacyLicensee, pharmacistLicense],
    BC: [pharmacistOwner, pharmacyLicensee],

    ON: [
      pharmacistOwner,
      {
        image: ACCREDITED_BY_LOGO_ON,
        imageAlt: formatMessage({ id: 'accreditedBy' }),
        title: formatMessage({ id: 'accreditedBy' }),
        subtitle: (
          <Link href={COLLEGE_LINKS.ON}>{formatMessage({ id: 'pharmacistInformationHelpTextCollegeON' })}</Link>
        ),
      },
    ],
  };

  // Select the card configuration based on the detected province
  const cards = ['AB', 'BC', 'ON'].includes(province) ? cardConfigurations[province] : [pharmacistOwner];

  const getPharmacistInformationHelpText = useMemo(() => {
    const helpTextConfig: any = {
      QC: {
        messageId: 'pharmacistInformationHelpTextQC',
        values: {},
      },
      BC: {
        messageId: 'pharmacistInformationHelpTextBC',
        values: {
          licencedBy: (
            <Link data-testid="college-link" href={COLLEGE_LINKS.BC}>
              {formatMessage({ id: 'pharmacistInformationHelpTextCollegeBC' })}
            </Link>
          ),
        },
      },
      default: {
        messageId: ['AB', 'ON'].includes(province)
          ? `pharmacistInformationHelpText${province}`
          : 'pharmacistInformationHelpTextROC',
        values: {
          link:
            province !== 'ROC' ? (
              <Link data-testid="college-link" href={COLLEGE_LINKS[province as keyof typeof COLLEGE_LINKS]}>
                {formatMessage({ id: 'here' })}
              </Link>
            ) : null,
        },
      },
    };

    let finalConfig = helpTextConfig.default;

    if (province === 'BC') {
      finalConfig = helpTextConfig.BC;
    }

    if ([STORE_TYPES.STTP_PSS, STORE_TYPES.PHX].includes(storeType)) {
      finalConfig = helpTextConfig.QC;
    }

    return formatMessage({ id: finalConfig.messageId }, finalConfig.values);
  }, [formatMessage, province, storeType]);

  return (
    <>
      <div className={styles.pharmacistInformation} data-testid="pharmacist-information">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Headline title={formatMessage({ id: 'pharmacistInformation' })} headlineSize={4} />
          {province === 'AB' && (
            <Link {...common?.common02Regular} href={`${PHARMACY_LICENSE}${storeId}.pdf`}>
              {`${formatMessage({ id: 'pharmacyLicenseNumber' })} ${
                pharmacistInformation?.data?.pharmacyLicenseNumber
              }`}
            </Link>
          )}
        </div>
        <Text
          {...common?.common02Regular}
          className={styles.informationText}
          data-testid="pharmacist-information-help-text"
        >
          {getPharmacistInformationHelpText}
        </Text>
      </div>
      <div className={styles.cardContainer} style={{ '--card-count': cards?.length || 1 } as React.CSSProperties}>
        {cards && cards.map((card, index) => <CardInformation key={index} {...card} storeId={storeId} />)}
      </div>
    </>
  );
};

export default PharmacistInformation;
